/**
 * ImpellerMixingPage.tsx
 * Real-time impeller mixing calculator with SVG impeller visualisation,
 * 3-option suggested settings modal, and live G/Re/P computation.
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  FlaskConical, Zap, Waves, Settings2, ChevronDown, ChevronUp,
  ArrowRight, LayoutDashboard, Info, CheckCircle2, AlertTriangle, Gauge,
  Beaker, Wind, RotateCcw, Lightbulb, X,
} from 'lucide-react';
import { loadDosingState } from './ChemicalDosingPage';
import type { DosingState } from './ChemicalDosingPage';

// ─── Mixing state ─────────────────────────────────────────────────────────────
export const MIXING_STATE_KEY = 'rb_impeller_mixing_v1';

export interface MixingConfig {
  impellerType: string;
  tankShape: 'cylindrical' | 'square' | 'rectangular';
  tankVolume: number;
  hdRatio: number;
  dtRatio: number;
  rpm: number;
  temperature: number;
  numberOfImpellers: number;
  baffled: boolean;
  liquidParams: Record<string, number>;
}

export interface MixingResults {
  N: number; D: number; T: number; H: number; V: number;
  rho: number; mu: number; Re: number; Np: number; P_kW: number;
  G: number; hrt_min: number; theta_m: number; Gt: number;
  flowRegime: string; mixingStatus: string;
}

export interface MixingState {
  config: MixingConfig;
  results: MixingResults;
  dosingStateSnapshot: DosingState | null;
  savedAt: string;
}

export function loadMixingState(): MixingState | null {
  try { const r = localStorage.getItem(MIXING_STATE_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveMixingState(s: MixingState): void {
  try { localStorage.setItem(MIXING_STATE_KEY, JSON.stringify(s)); } catch { /* noop */ }
}

// ─── Impeller library ─────────────────────────────────────────────────────────
export interface ImpellerSpec {
  id: string; name: string; shortName: string;
  Np: number; flowType: 'radial' | 'axial' | 'tangential';
  bestFor: string; optGMin: number; optGMax: number;
  color: string;   // tailwind text-* class
  svgColor: string; // hex for SVG
}

export const IMPELLERS: ImpellerSpec[] = [
  { id: 'rushton6',   name: 'Rushton 6-blade Disc Turbine', shortName: 'Rushton 6-blade', Np: 5.0,  flowType: 'radial',     bestFor: 'Gas dispersion, rapid coagulation',  optGMin: 500, optGMax: 1500, color: 'text-blue-400',   svgColor: '#3b82f6' },
  { id: 'rushton4',   name: 'Rushton 4-blade Disc Turbine', shortName: 'Rushton 4-blade', Np: 3.0,  flowType: 'radial',     bestFor: 'Coagulation, chemical dispersion',   optGMin: 400, optGMax: 1200, color: 'text-blue-300',   svgColor: '#60a5fa' },
  { id: 'ptb45',      name: 'Pitched-blade Turbine 45°',    shortName: 'PTB 45°',         Np: 1.27, flowType: 'axial',      bestFor: 'Flocculation, gentle blending',      optGMin: 20,  optGMax: 100,  color: 'text-emerald-400', svgColor: '#34d399' },
  { id: 'ptb60',      name: 'Pitched-blade Turbine 60°',    shortName: 'PTB 60°',         Np: 2.0,  flowType: 'axial',      bestFor: 'Solids suspension, flocculation',    optGMin: 30,  optGMax: 150,  color: 'text-emerald-300', svgColor: '#6ee7b7' },
  { id: 'marine',     name: 'Marine Propeller (3-blade)',    shortName: 'Marine Propeller',Np: 0.35, flowType: 'axial',      bestFor: 'Low-shear blending, floc growth',    optGMin: 10,  optGMax: 75,   color: 'text-violet-400',  svgColor: '#a78bfa' },
  { id: 'hydrofoil',  name: 'Hydrofoil A310 (Lightnin)',     shortName: 'Hydrofoil A310',  Np: 0.30, flowType: 'axial',      bestFor: 'Gentle flocculation, large tanks',   optGMin: 10,  optGMax: 60,   color: 'text-indigo-400',  svgColor: '#818cf8' },
  { id: 'anchor',     name: 'Anchor / Wall-sweep Paddle',    shortName: 'Anchor Paddle',   Np: 0.38, flowType: 'tangential', bestFor: 'Viscous mixing, wall contact',       optGMin: 5,   optGMax: 50,   color: 'text-orange-400',  svgColor: '#fb923c' },
  { id: 'gate',       name: 'Gate / Frame Paddle',           shortName: 'Gate Paddle',     Np: 0.35, flowType: 'tangential', bestFor: 'Slow flocculation, clarifiers',      optGMin: 5,   optGMax: 40,   color: 'text-amber-400',   svgColor: '#fbbf24' },
  { id: 'flatpaddle', name: 'Flat Paddle (2-blade)',         shortName: 'Flat Paddle',     Np: 1.80, flowType: 'tangential', bestFor: 'Moderate mixing, chemical dosing',   optGMin: 50,  optGMax: 300,  color: 'text-pink-400',    svgColor: '#f472b6' },
];

// ─── Physics helpers ──────────────────────────────────────────────────────────
function waterViscosity(T_C: number) { const T=T_C+273.15; return 2.414e-5*Math.pow(10,247.8/(T-140)); }
function waterDensity(T_C: number)   { return 999.842-0.0620*T_C-0.003821*T_C*T_C+4.2e-6*Math.pow(T_C,3); }

function tankDimensions(shape: string, vol: number, hdRatio: number) {
  if (shape==='cylindrical') { const D=Math.pow((4*vol)/(Math.PI*hdRatio),1/3); return { T:D, H:hdRatio*D, footprint:Math.PI/4*D*D }; }
  if (shape==='square')      { const W=Math.pow(vol/hdRatio,1/3);               return { T:W, H:hdRatio*W, footprint:W*W }; }
  const W=Math.pow(vol/(1.5*hdRatio),1/3); return { T:W, H:hdRatio*W, footprint:1.5*W*W };
}

function calcMixing(cfg: MixingConfig, flowRateMH = 50): MixingResults {
  const imp = IMPELLERS.find(i=>i.id===cfg.impellerType)??IMPELLERS[0];
  const {T,H} = tankDimensions(cfg.tankShape, cfg.tankVolume, cfg.hdRatio);
  const V=cfg.tankVolume, rho=waterDensity(cfg.temperature), mu=waterViscosity(cfg.temperature);
  const N=cfg.rpm/60, D=cfg.dtRatio*T;
  const Re=rho*N*D*D/mu;
  let Np_eff=imp.Np;
  if (Re<10) Np_eff=imp.Np*10/Re;
  else if (Re<1000) Np_eff=imp.Np*Math.pow(Re/1000,-0.1);
  const P_total=Np_eff*rho*Math.pow(N,3)*Math.pow(D,5)*cfg.numberOfImpellers;
  const P_kW=P_total/1000;
  const G=Math.sqrt(P_total/(mu*V));
  const theta_m=Math.max(0.1, 5.9*Math.pow(T/D,2)*(1/N)*Math.pow(H/T,0.5)/Math.pow(Math.max(Re,1),1/3));
  const hrt_min=flowRateMH>0?(V/(flowRateMH/60)):0;
  const Gt=G*hrt_min*60;
  let flowRegime='Turbulent (Re > 10 000)';
  if (Re<10) flowRegime='Laminar (Re < 10)';
  else if (Re<1000) flowRegime='Transitional (Re 10–1 000)';
  else if (Re<10000) flowRegime='Transitional-turbulent (Re 1 000–10 000)';
  let mixingStatus='Optimal';
  if (G<imp.optGMin) mixingStatus='Under-mixed (G too low)';
  else if (G>imp.optGMax) mixingStatus='Over-mixed (G too high — floc breakup risk)';
  return {N,D,T,H,V,rho,mu,Re,Np:Np_eff,P_kW,G,hrt_min,theta_m,Gt,flowRegime,mixingStatus};
}

// ─── Suggestion engine (3 options) ───────────────────────────────────────────
interface Suggestion {
  id: string;
  label: string;
  tag: string;
  tagColor: string;
  impellerType: string;
  rpm: number;
  dtRatio: number;
  tankVolume: number;
  numberOfImpellers: number;
  hdRatio: number;
  rationale: string;
}

function buildSuggestions(dosingState: DosingState | null): Suggestion[] {
  const chems = dosingState?.chemicals ?? [];
  const flowQ  = dosingState?.flowRateMH ?? 50;
  const hasCoag = chems.some(c=>['alum','pac','fecl3','feso4','fe2so4'].includes(c.id)&&c.enabled&&c.dose>0);
  const hasPoly = chems.some(c=>['anionic_poly','cationic_poly','nonionic_poly'].includes(c.id)&&c.enabled&&c.dose>0);
  const baseVol = (v: number) => Math.max(0.5, Math.ceil((flowQ/60)*v*2)/2);

  // Option A — Flash/Rapid mix (always Rushton)
  const optA: Suggestion = {
    id:'flash',
    label: 'Flash Coagulation Mix',
    tag: 'High Shear', tagColor: 'text-blue-300 border-blue-700/40 bg-blue-900/30',
    impellerType: hasCoag ? 'rushton4' : 'flatpaddle',
    rpm: hasCoag ? 150 : 120,
    dtRatio: 0.33,
    tankVolume: baseVol(hasCoag ? 5 : 8),
    numberOfImpellers: 1,
    hdRatio: 1.0,
    rationale: hasCoag
      ? 'Rushton 4-blade radial turbine for rapid coagulant dispersion. G ≈ 600–1 000 s⁻¹ ensures flash mixing within the first 30 s contact time.'
      : 'High-intensity flat paddle for rapid chemical dispersion. Short HRT maximises contact with added reagents.',
  };

  // Option B — Balanced mix (PTB 45°)
  const optB: Suggestion = {
    id:'balanced',
    label: 'Balanced Coag-Floc Mix',
    tag: 'Recommended', tagColor: 'text-emerald-300 border-emerald-700/40 bg-emerald-900/30',
    impellerType: (hasCoag && hasPoly) ? 'ptb45' : hasCoag ? 'ptb60' : 'ptb45',
    rpm: hasCoag && hasPoly ? 45 : hasCoag ? 80 : 60,
    dtRatio: 0.40,
    tankVolume: baseVol(hasCoag && hasPoly ? 15 : hasCoag ? 8 : 12),
    numberOfImpellers: (hasCoag && hasPoly) ? 2 : 1,
    hdRatio: 1.1,
    rationale: (hasCoag && hasPoly)
      ? 'PTB 45° dual-impeller: upper provides slow flocculation (G≈30–60 s⁻¹), lower provides coagulant dispersion (G≈200–400 s⁻¹). Gt 15 000–60 000 builds large settleable floc.'
      : 'PTB 45° balanced axial-radial flow. Moderate G gives good chemical dispersion while preserving fragile floc structure.',
  };

  // Option C — Gentle / slow mix (Hydrofoil or Marine)
  const optC: Suggestion = {
    id:'gentle',
    label: 'Gentle Flocculation Mix',
    tag: 'Low Shear', tagColor: 'text-purple-300 border-purple-700/40 bg-purple-900/30',
    impellerType: hasPoly ? 'hydrofoil' : 'marine',
    rpm: hasPoly ? 22 : 30,
    dtRatio: 0.45,
    tankVolume: baseVol(hasPoly ? 25 : 18),
    numberOfImpellers: 1,
    hdRatio: 1.2,
    rationale: hasPoly
      ? 'Hydrofoil A310 minimises turbulent shear (G ≈ 10–40 s⁻¹) to grow large, low-density floc. Extended HRT (20–30 min) achieves Gt 10 000–100 000 for optimal settling.'
      : 'Marine propeller provides gentle axial circulation with minimal shear. Suitable for fragile suspended solids and long contact-time processes.',
  };

  return [optA, optB, optC];
}

// ─── SVG Impeller Diagram ─────────────────────────────────────────────────────
function drawImpellerSVG(
  type: string,
  cx: number, cy: number,
  R: number,
  color: string,
): React.ReactNode {
  const hub = <circle cx={cx} cy={cy} r={Math.max(4, R*0.13)} fill={color} opacity={0.95}/>;

  switch (type) {
    case 'rushton6':
    case 'rushton4': {
      const n = type==='rushton6' ? 6 : 4;
      const bW = Math.max(3, R*0.14);
      const bH = Math.max(8, R*0.38);
      const disc = <rect x={cx-R} y={cy-4} width={2*R} height={8} rx={2} fill={color} opacity={0.95}/>;
      const blades = Array.from({length:n}).map((_,i) => {
        const tx = cx - R + (2*R*(i+0.5)/n) - bW/2;
        return <rect key={i} x={tx} y={cy-bH-4} width={bW} height={bH*2+8} rx={1.5} fill={color} opacity={0.7}/>;
      });
      return <>{blades}{disc}{hub}</>;
    }

    case 'ptb45':
    case 'ptb60': {
      const ang = type==='ptb45' ? 45 : 60;
      const bLen = R*0.82, bW = Math.max(4, R*0.18);
      return <>
        {[0,90,180,270].map((baseAng,i) => {
          const theta = (baseAng) * Math.PI/180;
          const mx = cx + Math.cos(theta)*R*0.45;
          const my = cy + Math.sin(theta)*R*0.45;
          return <rect key={i} x={mx-bLen/2} y={my-bW/2} width={bLen} height={bW}
            rx={2} fill={color} opacity={0.85}
            transform={`rotate(${baseAng+ang} ${mx} ${my})`}/>;
        })}
        {hub}
      </>;
    }

    case 'marine': {
      return <>
        {[0,120,240].map((ang,i) => {
          const t = (ang+20)*Math.PI/180;
          const ex = cx + Math.cos(t)*R*0.88;
          const ey = cy + Math.sin(t)*R*0.88;
          const ctrl1x = cx + Math.cos(t-0.6)*R*0.55;
          const ctrl1y = cy + Math.sin(t-0.6)*R*0.55;
          const ctrl2x = ex - Math.cos(t)*R*0.25;
          const ctrl2y = ey - Math.sin(t)*R*0.25;
          return <path key={i}
            d={`M ${cx} ${cy} C ${ctrl1x} ${ctrl1y} ${ctrl2x} ${ctrl2y} ${ex} ${ey}`}
            stroke={color} strokeWidth={Math.max(3,R*0.22)} fill="none" strokeLinecap="round" opacity={0.85}/>;
        })}
        {hub}
      </>;
    }

    case 'hydrofoil': {
      return <>
        {[0,120,240].map((ang,i) => {
          const t = (ang+10)*Math.PI/180;
          const mx = cx + Math.cos(t+0.18)*R*0.46;
          const my = cy + Math.sin(t+0.18)*R*0.46;
          return <ellipse key={i} cx={mx} cy={my} rx={R*0.62} ry={R*0.18}
            fill={color} opacity={0.82}
            transform={`rotate(${ang+10} ${mx} ${my})`}/>;
        })}
        {hub}
      </>;
    }

    case 'anchor': {
      const aw = R*1.82;  // arm half-width (close to tank wall)
      const ah = R*1.3;   // arm height
      return <>
        {/* Vertical arms */}
        <rect x={cx-aw-R*0.09} y={cy-ah/2} width={R*0.18} height={ah} rx={2} fill={color} opacity={0.85}/>
        <rect x={cx+aw-R*0.09} y={cy-ah/2} width={R*0.18} height={ah} rx={2} fill={color} opacity={0.85}/>
        {/* Bottom bar */}
        <rect x={cx-aw} y={cy+ah/2-R*0.09} width={aw*2} height={R*0.18} rx={2} fill={color} opacity={0.85}/>
        {/* Top crossbar */}
        <rect x={cx-aw} y={cy-ah/2-R*0.09} width={aw*2} height={R*0.18} rx={2} fill={color} opacity={0.6}/>
        {hub}
      </>;
    }

    case 'gate': {
      const fw = R*1.7, fh = R*1.2, sw = Math.max(3, R*0.14);
      return <>
        <rect x={cx-fw} y={cy-fh} width={fw*2} height={fh*2} rx={2}
          stroke={color} strokeWidth={sw} fill="none" opacity={0.85}/>
        <line x1={cx-fw} y1={cy} x2={cx+fw} y2={cy} stroke={color} strokeWidth={sw*0.7} opacity={0.65}/>
        <line x1={cx} y1={cy-fh} x2={cx} y2={cy+fh} stroke={color} strokeWidth={sw*0.7} opacity={0.65}/>
        {hub}
      </>;
    }

    case 'flatpaddle': {
      const ph = Math.max(5, R*0.2);
      return <>
        <rect x={cx-R} y={cy-ph/2} width={R*0.82} height={ph} rx={2} fill={color} opacity={0.88}/>
        <rect x={cx+R*0.18} y={cy-ph/2} width={R*0.82} height={ph} rx={2} fill={color} opacity={0.88}/>
        {hub}
      </>;
    }

    default:
      return <><circle cx={cx} cy={cy} r={R} fill={color} opacity={0.3}/>{hub}</>;
  }
}

// ─── Tank + Impeller SVG Diagram ──────────────────────────────────────────────
interface DiagramProps {
  impellerType: string;
  dtRatio: number;
  numberOfImpellers: number;
  hdRatio: number;
  tankShape: 'cylindrical' | 'square' | 'rectangular';
  baffled: boolean;
  T_m: number;    // actual tank diameter/width in metres
  D_m: number;    // actual impeller diameter in metres
  H_m: number;    // actual tank height in metres
  onDtChange: (v: number) => void;
}

function ImpellerDiagram({
  impellerType, dtRatio, numberOfImpellers, hdRatio,
  tankShape, baffled, T_m, D_m, H_m, onDtChange,
}: DiagramProps) {
  const imp = IMPELLERS.find(i=>i.id===impellerType)??IMPELLERS[0];

  // SVG layout constants
  const svgW = 240;
  const T_px = 160;  // tank always 160px wide on screen
  const cx = svgW / 2;  // 120
  const tankX1 = cx - T_px/2;   // 40
  const tankX2 = cx + T_px/2;   // 200

  const maxH_px = 200;
  const minH_px = 70;
  const H_px = Math.max(minH_px, Math.min(maxH_px, T_px * Math.min(hdRatio, 2.5)));
  const tankTop    = 18;
  const tankBottom = tankTop + H_px;
  const svgH = tankBottom + 58;   // room for dimension line + DT label + D line

  // Impeller positions (fraction from bottom)
  const nImp = Math.max(1, Math.min(4, numberOfImpellers));
  const fracPositions = [0.28, 0.56, 0.74, 0.88].slice(0, nImp);
  const impellerYs = fracPositions.map(f => tankBottom - f * H_px);

  // Impeller radius in pixels
  const R_px = (dtRatio * T_px) / 2;

  // Tank outline path
  const tankPath = tankShape === 'cylindrical'
    ? `M ${tankX1} ${tankTop} L ${tankX1} ${tankBottom} Q ${cx} ${tankBottom+10} ${tankX2} ${tankBottom} L ${tankX2} ${tankTop}`
    : `M ${tankX1} ${tankTop} L ${tankX1} ${tankBottom} L ${tankX2} ${tankBottom} L ${tankX2} ${tankTop}`;

  // Draggable D/T handle on dimension line
  const dimY = tankBottom + 28;
  const handleX = cx + R_px;

  return (
    <div className="select-none">
      <svg
        viewBox={`0 0 ${svgW} ${svgH}`}
        width="100%"
        style={{ height: `${svgH}px`, cursor: 'default' }}
        aria-label="Impeller and tank diagram"
      >
        <defs>
          <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="#64748b"/>
          </marker>
          <marker id="arrW" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto-start-reverse">
            <path d="M0,0 L0,6 L6,3 z" fill="#64748b"/>
          </marker>
          <linearGradient id="liquidGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.5"/>
            <stop offset="100%" stopColor="#0f2342" stopOpacity="0.7"/>
          </linearGradient>
        </defs>

        {/* Liquid fill */}
        <clipPath id="tankClip">
          <rect x={tankX1+2} y={tankTop+1} width={T_px-4} height={H_px+5}/>
        </clipPath>
        <rect x={tankX1+2} y={tankTop+1} width={T_px-4} height={H_px-2}
          fill="url(#liquidGrad)" clipPath="url(#tankClip)"/>

        {/* Baffles */}
        {baffled && [tankX1+7, tankX2-7].map((bx,bi) => (
          <rect key={bi} x={bx-3} y={tankTop+2} width={6} height={H_px-4}
            fill="#334155" stroke="#475569" strokeWidth={0.5} opacity={0.9}/>
        ))}

        {/* Shaft */}
        <line x1={cx} y1={tankTop+1} x2={cx} y2={tankBottom-2}
          stroke="#94a3b8" strokeWidth={3} strokeLinecap="round"/>

        {/* Impellers */}
        {impellerYs.map((iy, i) => (
          <g key={i}>
            {drawImpellerSVG(impellerType, cx, iy, R_px, imp.svgColor)}
          </g>
        ))}

        {/* Tank walls (on top so they clip the impeller if it overflows) */}
        <path d={tankPath} stroke="#475569" strokeWidth={2.5} fill="none" strokeLinejoin="round"/>
        {/* Tank top (open) — dashed line */}
        <line x1={tankX1} y1={tankTop} x2={tankX2} y2={tankTop}
          stroke="#475569" strokeWidth={1.5} strokeDasharray="4 3"/>

        {/* ─ T dimension line ─ */}
        <line x1={tankX1} y1={dimY} x2={tankX2} y2={dimY}
          stroke="#64748b" strokeWidth={1}
          markerStart="url(#arrW)" markerEnd="url(#arr)"/>
        <line x1={tankX1} y1={tankBottom+4} x2={tankX1} y2={dimY+4}
          stroke="#475569" strokeWidth={0.8} strokeDasharray="3 2"/>
        <line x1={tankX2} y1={tankBottom+4} x2={tankX2} y2={dimY+4}
          stroke="#475569" strokeWidth={0.8} strokeDasharray="3 2"/>
        <rect x={cx-26} y={dimY-8} width={52} height={13} rx={3}
          fill="#0f172a" opacity={0.85}/>
        <text x={cx} y={dimY+2} textAnchor="middle" fontSize="9" fill="#94a3b8" fontFamily="monospace">
          T = {T_m.toFixed(2)}m
        </text>

        {/* ─ D dimension line (at top impeller) ─ */}
        {impellerYs[0] !== undefined && (() => {
          const iy = impellerYs[0];
          const dLineY = iy - Math.max(16, R_px * 0.55);
          return (
            <>
              <line x1={cx-R_px} y1={dLineY} x2={cx+R_px} y2={dLineY}
                stroke={imp.svgColor} strokeWidth={1} opacity={0.8}
                markerStart="url(#arrW)" markerEnd="url(#arr)"/>
              <line x1={cx-R_px} y1={iy-4} x2={cx-R_px} y2={dLineY+4}
                stroke={imp.svgColor} strokeWidth={0.8} strokeDasharray="2 2" opacity={0.6}/>
              <line x1={cx+R_px} y1={iy-4} x2={cx+R_px} y2={dLineY+4}
                stroke={imp.svgColor} strokeWidth={0.8} strokeDasharray="2 2" opacity={0.6}/>
              <rect x={cx-22} y={dLineY-9} width={44} height={12} rx={2.5}
                fill="#0f172a" opacity={0.9}/>
              <text x={cx} y={dLineY+1} textAnchor="middle" fontSize="8.5" fill={imp.svgColor} fontFamily="monospace" fontWeight="bold">
                D={D_m>=1?(D_m.toFixed(2)+'m'):(Math.round(D_m*1000)+'mm')}
              </text>
            </>
          );
        })()}

        {/* H dimension line (right side) */}
        {(() => {
          const hx = tankX2 + 12;
          return (
            <>
              <line x1={hx} y1={tankTop} x2={hx} y2={tankBottom}
                stroke="#64748b" strokeWidth={1}
                markerStart="url(#arrW)" markerEnd="url(#arr)"/>
              <line x1={tankX2+2} y1={tankTop} x2={hx+4} y2={tankTop}
                stroke="#475569" strokeWidth={0.8} strokeDasharray="3 2"/>
              <line x1={tankX2+2} y1={tankBottom} x2={hx+4} y2={tankBottom}
                stroke="#475569" strokeWidth={0.8} strokeDasharray="3 2"/>
              <rect x={hx+3} y={(tankTop+tankBottom)/2-8} width={34} height={12} rx={2.5}
                fill="#0f172a" opacity={0.85}/>
              <text x={hx+20} y={(tankTop+tankBottom)/2+2} textAnchor="middle"
                fontSize="8.5" fill="#94a3b8" fontFamily="monospace">
                H={H_m>=1?H_m.toFixed(2)+'m':Math.round(H_m*100)+'cm'}
              </text>
            </>
          );
        })()}

        {/* Impeller count label */}
        {nImp > 1 && (
          <text x={tankX1-2} y={tankTop+10} textAnchor="end" fontSize="8" fill="#64748b" fontStyle="italic">
            ×{nImp}
          </text>
        )}

        {/* Draggable D/T handle */}
        <g style={{cursor:'ew-resize'}} onPointerDown={e => {
          const svgEl = (e.currentTarget as SVGGElement).ownerSVGElement!;
          const rect2 = svgEl.getBoundingClientRect();
          const scaleX = svgW / rect2.width;
          const onMove = (ev: PointerEvent) => {
            const relX = (ev.clientX - rect2.left) * scaleX;
            const newR = Math.abs(relX - cx);
            const newDT = Math.max(0.10, Math.min(0.70, newR / (T_px/2)));
            onDtChange(Math.round(newDT*100)/100);
          };
          const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
          e.currentTarget.setPointerCapture(e.pointerId);
        }}>
          <circle cx={handleX} cy={impellerYs[0] ?? tankBottom-40} r={7}
            fill={imp.svgColor} opacity={0.9} stroke="#fff" strokeWidth={1.5}/>
          <text x={handleX} y={(impellerYs[0] ?? tankBottom-40)+4}
            textAnchor="middle" fontSize="8" fill="#fff" fontWeight="bold">⟺</text>
        </g>
        <g style={{cursor:'ew-resize'}} onPointerDown={e => {
          const svgEl = (e.currentTarget as SVGGElement).ownerSVGElement!;
          const rect2 = svgEl.getBoundingClientRect();
          const scaleX = svgW / rect2.width;
          const onMove = (ev: PointerEvent) => {
            const relX = (ev.clientX - rect2.left) * scaleX;
            const newR = Math.abs(relX - cx);
            const newDT = Math.max(0.10, Math.min(0.70, newR / (T_px/2)));
            onDtChange(Math.round(newDT*100)/100);
          };
          const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
          e.currentTarget.setPointerCapture(e.pointerId);
        }}>
          <circle cx={cx-R_px} cy={impellerYs[0] ?? tankBottom-40} r={7}
            fill={imp.svgColor} opacity={0.9} stroke="#fff" strokeWidth={1.5}/>
          <text x={cx-R_px} y={(impellerYs[0] ?? tankBottom-40)+4}
            textAnchor="middle" fontSize="8" fill="#fff" fontWeight="bold">⟺</text>
        </g>
      </svg>

      {/* D/T drag hint */}
      <p className="text-center text-[10px] text-slate-600 mt-1">
        Drag <span style={{color: imp.svgColor}}>●</span> handles to adjust D/T · D/T = {dtRatio.toFixed(2)}
      </p>
    </div>
  );
}

// ─── Suggestion Modal (3 options) ─────────────────────────────────────────────
function SuggestionModal({ suggestions, flowRateMH, onApply, onClose }: {
  suggestions: Suggestion[];
  flowRateMH: number;
  onApply: (s: Suggestion) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string>(suggestions[1]?.id ?? suggestions[0]?.id);
  const fmt = (v: number, d=1) => isFinite(v)&&!isNaN(v)?v.toFixed(d):'—';
  const fmtSI = (v: number) => { if(v>=1e6) return (v/1e6).toFixed(1)+'M'; if(v>=1e3) return (v/1e3).toFixed(0)+'k'; return v.toFixed(0); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
      <div className="bg-slate-900 border border-purple-700/60 rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/60">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-purple-400"/>
            <span className="text-base font-bold text-purple-200">Suggested Mixing Options</span>
            <span className="text-xs text-slate-500 ml-1">Q = {flowRateMH} m³/h</span>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4"/>
          </button>
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 overflow-y-auto">
          {suggestions.map(s => {
            const imp = IMPELLERS.find(i=>i.id===s.impellerType)??IMPELLERS[0];
            const fakeCfg: MixingConfig = {
              impellerType: s.impellerType, tankShape:'cylindrical',
              tankVolume:s.tankVolume, hdRatio:s.hdRatio, dtRatio:s.dtRatio,
              rpm:s.rpm, temperature:20, numberOfImpellers:s.numberOfImpellers,
              baffled:true, liquidParams:{},
            };
            const res = calcMixing(fakeCfg, flowRateMH);
            const isSelected = selected === s.id;
            const gOk = res.G>=imp.optGMin && res.G<=imp.optGMax;
            const gtOk = res.Gt>=1000 && res.Gt<=100000;

            return (
              <button
                key={s.id}
                onClick={() => setSelected(s.id)}
                className={`text-left rounded-xl border p-4 transition-all ${
                  isSelected
                    ? 'border-purple-500 bg-purple-900/25 ring-1 ring-purple-500/40'
                    : 'border-slate-700/60 bg-slate-800/40 hover:border-slate-600'
                }`}
              >
                {/* Tag */}
                <div className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border mb-2 ${s.tagColor}`}>
                  {s.tag}
                </div>
                {/* Title */}
                <div className={`text-sm font-semibold mb-1 ${isSelected?'text-purple-200':'text-slate-200'}`}>
                  {s.label}
                </div>
                {/* Mini SVG */}
                <div className="flex justify-center my-2 bg-slate-900/60 rounded-lg p-1">
                  <svg viewBox="0 0 80 60" width="80" height="60">
                    {/* mini tank */}
                    <rect x={10} y={5} width={60} height={50} rx={2} stroke="#475569" strokeWidth={1.5} fill="#0f172a" fillOpacity={0.6}/>
                    {/* shaft */}
                    <line x1={40} y1={5} x2={40} y2={55} stroke="#64748b" strokeWidth={1.5}/>
                    {/* impeller */}
                    {drawImpellerSVG(s.impellerType, 40, 38, 60*s.dtRatio/2, imp.svgColor)}
                  </svg>
                </div>
                {/* Impeller name */}
                <div className={`text-xs font-medium mb-2 ${imp.color}`}>{imp.name}</div>
                {/* Config */}
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between text-slate-400">
                    <span>Speed</span><span className="text-slate-200 font-mono">{s.rpm} RPM</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>D/T</span><span className="text-slate-200 font-mono">{s.dtRatio.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Volume</span><span className="text-slate-200 font-mono">{s.tankVolume} m³</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Impellers</span><span className="text-slate-200 font-mono">{s.numberOfImpellers}</span>
                  </div>
                </div>
                {/* Computed results */}
                <div className="mt-2 pt-2 border-t border-slate-700/40 grid grid-cols-2 gap-1 text-xs">
                  <div className={`text-center rounded px-1 py-1 ${gOk?'bg-emerald-900/30 text-emerald-300':'bg-amber-900/30 text-amber-300'}`}>
                    <div className="text-[9px] text-slate-500">G (s⁻¹)</div>
                    <div className="font-bold font-mono">{fmt(res.G,0)}</div>
                  </div>
                  <div className={`text-center rounded px-1 py-1 ${gtOk?'bg-emerald-900/30 text-emerald-300':'bg-amber-900/30 text-amber-300'}`}>
                    <div className="text-[9px] text-slate-500">Gt</div>
                    <div className="font-bold font-mono">{fmtSI(res.Gt)}</div>
                  </div>
                  <div className="text-center rounded px-1 py-1 bg-slate-800/40 text-slate-300">
                    <div className="text-[9px] text-slate-500">P (kW)</div>
                    <div className="font-bold font-mono">{fmt(res.P_kW,3)}</div>
                  </div>
                  <div className="text-center rounded px-1 py-1 bg-slate-800/40 text-slate-300">
                    <div className="text-[9px] text-slate-500">HRT (min)</div>
                    <div className="font-bold font-mono">{fmt(res.hrt_min,1)}</div>
                  </div>
                </div>
                {isSelected && (
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-purple-300">
                    <CheckCircle2 className="w-3 h-3"/> Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Rationale for selected */}
        {(() => {
          const sel = suggestions.find(s=>s.id===selected);
          if (!sel) return null;
          return (
            <div className="mx-4 mb-3 bg-slate-800/50 border border-slate-700/40 rounded-xl p-3 text-xs text-slate-400 leading-relaxed">
              <span className="text-slate-300 font-medium">Engineering rationale: </span>{sel.rationale}
            </div>
          );
        })()}

        {/* Footer */}
        <div className="flex gap-3 px-4 pb-4">
          <button
            onClick={() => { const sel=suggestions.find(s=>s.id===selected); if(sel) onApply(sel); }}
            className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4"/> Apply Selected Option
          </button>
          <button onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-600 hover:border-slate-500 text-slate-300 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (v: number, d=2) => isFinite(v)&&!isNaN(v)?v.toFixed(d):'—';
const fmtSI = (v: number) => { if(!isFinite(v)||isNaN(v)) return '—'; if(v>=1e6) return (v/1e6).toFixed(2)+'M'; if(v>=1e3) return (v/1e3).toFixed(1)+'k'; return v.toFixed(1); };

const PARAM_META: Record<string, { label: string; unit: string; min: number; max: number; step: number }> = {
  pH:          { label:'pH',            unit:'',       min:0,   max:14,    step:0.1 },
  turbidity:   { label:'Turbidity',     unit:'NTU',   min:0,   max:5000,  step:1 },
  tss:         { label:'TSS',           unit:'mg/L',  min:0,   max:10000, step:1 },
  cod:         { label:'COD',           unit:'mg/L',  min:0,   max:10000, step:1 },
  bod:         { label:'BOD',           unit:'mg/L',  min:0,   max:5000,  step:1 },
  tn:          { label:'TN',            unit:'mg/L',  min:0,   max:500,   step:0.1 },
  tp:          { label:'TP',            unit:'mg/L',  min:0,   max:100,   step:0.1 },
  nh3:         { label:'NH₃-N',         unit:'mg/L',  min:0,   max:500,   step:0.1 },
  tds:         { label:'TDS',           unit:'mg/L',  min:0,   max:50000, step:1 },
  alkalinity:  { label:'Alkalinity',    unit:'mg/L',  min:0,   max:2000,  step:1 },
  hardness:    { label:'Total Hardness',unit:'mg/L',  min:0,   max:2000,  step:1 },
  conductivity:{ label:'Conductivity',  unit:'µS/cm', min:0,   max:100000,step:10 },
  temperature: { label:'Temperature',   unit:'°C',    min:0,   max:100,   step:0.5 },
  oilgrease:   { label:'Oil & Grease',  unit:'mg/L',  min:0,   max:5000,  step:1 },
};

function StatBox({ label, value, unit, color='slate', sub }: { label:string; value:string; unit:string; color?:string; sub?:string }) {
  const bg: Record<string,string> = { slate:'bg-slate-800/60 border-slate-700/50', amber:'bg-amber-900/30 border-amber-700/40', green:'bg-emerald-900/30 border-emerald-700/40', red:'bg-red-900/30 border-red-700/40', blue:'bg-blue-900/30 border-blue-700/40', purple:'bg-purple-900/30 border-purple-700/40' };
  const txt: Record<string,string> = { slate:'text-slate-100', amber:'text-amber-300', green:'text-emerald-300', red:'text-red-300', blue:'text-blue-300', purple:'text-purple-300' };
  return (
    <div className={`rounded-lg border p-3 ${bg[color]??bg.slate}`}>
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className={`text-xl font-bold font-mono ${txt[color]??txt.slate}`}>
        {value} <span className="text-sm font-normal text-slate-400">{unit}</span>
      </div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ImpellerCard({ spec, selected, onSelect }: { spec:ImpellerSpec; selected:boolean; onSelect:()=>void }) {
  return (
    <button onClick={onSelect}
      className={`text-left rounded-xl border p-3 transition-all w-full ${selected?'border-amber-500 bg-amber-900/30 ring-1 ring-amber-500/40':'border-slate-700/50 bg-slate-800/40 hover:border-slate-600'}`}
    >
      {/* Mini inline SVG */}
      <div className="flex items-center gap-2 mb-1.5">
        <svg viewBox="0 0 36 36" width="36" height="36" className="shrink-0 rounded-lg bg-slate-900/60">
          {drawImpellerSVG(spec.id, 18, 18, 13, spec.svgColor)}
        </svg>
        <div className="min-w-0">
          <span className={`text-xs font-semibold ${selected?'text-amber-300':'text-slate-200'} leading-tight block`}>{spec.shortName}</span>
          <div className="flex gap-2 text-[10px] text-slate-400 mt-0.5">
            <span>Np={spec.Np}</span>
            <span className="capitalize">{spec.flowType}</span>
          </div>
        </div>
        {selected && <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 ml-auto shrink-0"/>}
      </div>
      <div className="text-[10px] text-slate-500 leading-tight">{spec.bestFor}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">G: {spec.optGMin}–{spec.optGMax} s⁻¹</div>
    </button>
  );
}

// ─── Default config ───────────────────────────────────────────────────────────
const DEFAULT_CONFIG: MixingConfig = {
  impellerType:'rushton4', tankShape:'cylindrical', tankVolume:2.0,
  hdRatio:1.0, dtRatio:0.33, rpm:120, temperature:20,
  numberOfImpellers:1, baffled:true, liquidParams:{},
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ImpellerMixingPage() {
  const navigate = useNavigate();
  const dosingState = useMemo(()=>loadDosingState(),[]);
  const flowRateMH  = dosingState?.flowRateMH ?? 50;

  const initialLiquidParams = useMemo(()=>dosingState?.postDosingParams??{},[dosingState]);

  const [cfg, setCfg] = useState<MixingConfig>({
    ...DEFAULT_CONFIG,
    temperature: (initialLiquidParams.temperature as number)??20,
    liquidParams: initialLiquidParams,
  });
  const [liquidParams, setLiquidParams] = useState<Record<string,number>>(initialLiquidParams);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showImpellerPanel, setShowImpellerPanel] = useState(true);
  const [showTankPanel, setShowTankPanel] = useState(true);
  const [showParamsPanel, setShowParamsPanel] = useState(false);

  const results        = useMemo(()=>calcMixing({...cfg,liquidParams},flowRateMH),[cfg,liquidParams,flowRateMH]);
  const selectedImpeller = IMPELLERS.find(i=>i.id===cfg.impellerType)??IMPELLERS[0];
  const suggestions    = useMemo(()=>buildSuggestions(dosingState),[dosingState]);
  const tankDims       = useMemo(()=>tankDimensions(cfg.tankShape,cfg.tankVolume,cfg.hdRatio),[cfg]);

  const setField = useCallback(<K extends keyof MixingConfig>(k:K,v:MixingConfig[K])=>{
    setCfg(prev=>({...prev,[k]:v}));
  },[]);

  const applySuggestion = (s: Suggestion) => {
    setCfg(prev=>({...prev,
      impellerType:s.impellerType, rpm:s.rpm, dtRatio:s.dtRatio,
      tankVolume:s.tankVolume, numberOfImpellers:s.numberOfImpellers, hdRatio:s.hdRatio,
    }));
    setShowSuggestions(false);
  };

  const handleProceedToTrain = () => {
    saveMixingState({ config:{...cfg,liquidParams}, results, dosingStateSnapshot:dosingState, savedAt:new Date().toISOString() });
    navigate('/dosing-mixing-train');
  };

  const gColor = results.mixingStatus==='Optimal'?'green':results.mixingStatus.includes('Under')?'amber':'red';
  const gtColor = results.Gt>0 ? (results.Gt<1000?'amber':results.Gt<=100000?'green':'red') : 'slate';
  const gtLabel = results.Gt>0 ? (results.Gt<1000?'Too low':results.Gt<=100000?'Optimal (10k–100k)':'Excessive') : '—';

  const VOLUME_PRESETS = [0.5,1,2,5,10,20,50,100,200,500];
  const RPM_PRESETS    = [10,20,30,60,120,200,300,600];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur border-b border-slate-700/50">
        <div className="max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Waves className="w-5 h-5 text-white"/>
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-100 leading-none">Impeller Mixing Calculator</h1>
              <div className="text-xs text-slate-400 mt-0.5">
                {dosingState?`Inlet: ${dosingState.influentSampleName} · Q = ${flowRateMH} m³/h`:'No dosing data — enter parameters manually'}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={()=>setShowSuggestions(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium transition-colors">
              <Lightbulb className="w-4 h-4"/> Suggested Settings
            </button>
            <button onClick={()=>navigate('/chemical-dosing')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-600 hover:border-slate-500 text-slate-300 text-sm transition-colors">
              ← Dosing
            </button>
            <button onClick={handleProceedToTrain}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-semibold transition-colors">
              View Train <ArrowRight className="w-4 h-4"/>
            </button>
          </div>
        </div>
      </div>

      {/* Suggestion Modal */}
      {showSuggestions && (
        <SuggestionModal
          suggestions={suggestions}
          flowRateMH={flowRateMH}
          onApply={applySuggestion}
          onClose={()=>setShowSuggestions(false)}
        />
      )}

      <div className="max-w-screen-2xl mx-auto px-4 py-6 grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* ── LEFT COLUMN ────────────────────────────────────────────────── */}
        <div className="xl:col-span-1 space-y-4">

          {/* SVG Impeller Visualisation */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-3 h-3 rounded-full`} style={{background: selectedImpeller.svgColor}}/>
              <h2 className="text-sm font-semibold text-slate-200">Tank & Impeller Diagram</h2>
              <span className={`ml-auto text-xs font-medium ${selectedImpeller.color}`}>{selectedImpeller.shortName}</span>
            </div>
            <ImpellerDiagram
              impellerType={cfg.impellerType}
              dtRatio={cfg.dtRatio}
              numberOfImpellers={cfg.numberOfImpellers}
              hdRatio={cfg.hdRatio}
              tankShape={cfg.tankShape}
              baffled={cfg.baffled}
              T_m={tankDims.T}
              D_m={results.D}
              H_m={tankDims.H}
              onDtChange={v=>setField('dtRatio',v)}
            />
            {/* D/T fine slider */}
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400">Impeller diameter D/T ratio</span>
                <span className="font-mono font-bold" style={{color:selectedImpeller.svgColor}}>{cfg.dtRatio.toFixed(2)}</span>
              </div>
              <input type="range" min={0.10} max={0.70} step={0.01} value={cfg.dtRatio}
                onChange={e=>setField('dtRatio',+e.target.value)} className="w-full accent-amber-400"/>
              <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                <span>0.10</span><span>Typical 0.25–0.45</span><span>0.70</span>
              </div>
            </div>
          </div>

          {/* Live Results */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-amber-400"/>
              <h2 className="text-sm font-semibold text-slate-200">Live Mixing Results</h2>
              <div className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium border ${
                results.mixingStatus==='Optimal'?'bg-emerald-900/50 text-emerald-300 border-emerald-700/40':
                results.mixingStatus.includes('Under')?'bg-amber-900/50 text-amber-300 border-amber-700/40':
                'bg-red-900/50 text-red-300 border-red-700/40'}`}>
                {results.mixingStatus==='Optimal'?'Optimal':results.mixingStatus.includes('Under')?'Under-mixed':'Over-mixed'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <StatBox label="Reynolds Number" value={fmtSI(results.Re)} unit="" color="blue" sub={results.flowRegime}/>
              <StatBox label="Power Number Np"  value={fmt(results.Np,2)} unit="" color="purple"/>
              <StatBox label="Power (P)"         value={fmt(results.P_kW,3)} unit="kW" color="amber"/>
              <StatBox label="Velocity Gradient G" value={fmt(results.G,1)} unit="s⁻¹" color={gColor}
                sub={`Opt: ${selectedImpeller.optGMin}–${selectedImpeller.optGMax} s⁻¹`}/>
              <StatBox label="Mixing Time θm"   value={fmt(results.theta_m,1)} unit="s" color="slate"/>
              <StatBox label="Camp Number Gt"    value={fmtSI(results.Gt)} unit="" color={gtColor} sub={gtLabel}/>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <StatBox label="HRT"          value={fmt(results.hrt_min,1)} unit="min" color="slate"/>
              <StatBox label="Impeller Ø D" value={fmt(results.D*1000,0)} unit="mm" color="slate"/>
              <StatBox label="Tank Ø T"     value={fmt(results.T*1000,0)} unit="mm" color="slate"/>
            </div>
          </div>

          {/* Benchmarks */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-4 h-4 text-blue-400"/>
              <h2 className="text-sm font-semibold text-slate-200">Industry Benchmarks</h2>
            </div>
            <div className="space-y-1.5 text-xs">
              {[
                {label:'Flash mixing (coagulation)', g:'300–1 500 s⁻¹', gt:'< 5 000',        t:'10–30 s'},
                {label:'Rapid mixing',                g:'100–300 s⁻¹',  gt:'5 000–15 000',   t:'1–5 min'},
                {label:'Slow mixing (flocculation)',  g:'10–75 s⁻¹',   gt:'10 000–100 000', t:'10–30 min'},
                {label:'Gentle blending',             g:'< 10 s⁻¹',    gt:'N/A',            t:'> 30 min'},
              ].map(r=>(
                <div key={r.label} className="bg-slate-800/40 rounded-lg px-3 py-2">
                  <div className="text-slate-300 font-medium">{r.label}</div>
                  <div className="flex gap-3 mt-0.5 text-slate-500">
                    <span>G: {r.g}</span><span>Gt: {r.gt}</span><span>t: {r.t}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active chemicals */}
          {dosingState && (
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <FlaskConical className="w-4 h-4 text-green-400"/>
                <h2 className="text-sm font-semibold text-slate-200">Active Chemical Doses</h2>
              </div>
              <div className="space-y-1">
                {dosingState.chemicals.filter(c=>c.enabled&&c.dose>0).map(c=>(
                  <div key={c.id} className="flex justify-between bg-slate-800/40 rounded-lg px-3 py-1.5 text-xs">
                    <span className="text-slate-300 capitalize">{c.id.replace(/_/g,' ')}</span>
                    <div className="flex gap-2 text-slate-400">
                      <span>{c.dose} mg/L</span>
                      <span className="text-slate-500">{((c.dose/1000)*flowRateMH).toFixed(2)} kg/h</span>
                    </div>
                  </div>
                ))}
                {dosingState.chemicals.filter(c=>c.enabled&&c.dose>0).length===0 && (
                  <p className="text-xs text-slate-500">No chemicals dosed</p>
                )}
              </div>
              <div className="mt-2 pt-2 border-t border-slate-700/40 flex justify-between text-xs text-slate-400">
                <span>Flow rate</span><span className="text-slate-300">{flowRateMH} m³/h</span>
              </div>
            </div>
          )}
        </div>

        {/* ── MIDDLE COLUMN: Controls ────────────────────────────────────── */}
        <div className="xl:col-span-1 space-y-4">

          {/* Impeller selection */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
            <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition-colors"
              onClick={()=>setShowImpellerPanel(p=>!p)}>
              <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-amber-400"/>
                <span className="text-sm font-semibold text-slate-200">Impeller Selection</span>
                <span className={`text-xs ml-1 ${selectedImpeller.color}`}>{selectedImpeller.shortName}</span>
              </div>
              {showImpellerPanel?<ChevronUp className="w-4 h-4 text-slate-400"/>:<ChevronDown className="w-4 h-4 text-slate-400"/>}
            </button>
            {showImpellerPanel && (
              <div className="px-4 pb-4 grid grid-cols-1 gap-2">
                {IMPELLERS.map(imp=>(
                  <ImpellerCard key={imp.id} spec={imp} selected={cfg.impellerType===imp.id}
                    onSelect={()=>setField('impellerType',imp.id)}/>
                ))}
              </div>
            )}
          </div>

          {/* Agitation controls */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-amber-400"/>
              <h2 className="text-sm font-semibold text-slate-200">Agitation Controls</h2>
            </div>
            {/* RPM */}
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-slate-400">Agitator Speed (RPM)</span>
                <span className="text-amber-300 font-mono font-bold">{cfg.rpm} RPM</span>
              </div>
              <input type="range" min={1} max={1000} step={1} value={cfg.rpm}
                onChange={e=>setField('rpm',+e.target.value)} className="w-full accent-amber-400"/>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {RPM_PRESETS.map(r=>(
                  <button key={r} onClick={()=>setField('rpm',r)}
                    className={`text-xs px-2 py-0.5 rounded border transition-colors ${cfg.rpm===r?'border-amber-500 bg-amber-900/40 text-amber-300':'border-slate-700 text-slate-400 hover:border-slate-500'}`}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
            {/* Temperature */}
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-slate-400">Liquid Temperature</span>
                <span className="text-amber-300 font-mono font-bold">{cfg.temperature} °C</span>
              </div>
              <input type="range" min={5} max={80} step={0.5} value={cfg.temperature}
                onChange={e=>setField('temperature',+e.target.value)} className="w-full accent-amber-400"/>
            </div>
            {/* Number of impellers */}
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-slate-400">Number of Impellers</span>
                <span className="text-amber-300 font-mono font-bold">{cfg.numberOfImpellers}</span>
              </div>
              <div className="flex gap-2">
                {[1,2,3,4].map(n=>(
                  <button key={n} onClick={()=>setField('numberOfImpellers',n)}
                    className={`flex-1 py-1.5 rounded-lg border text-sm font-medium transition-colors ${cfg.numberOfImpellers===n?'border-amber-500 bg-amber-900/40 text-amber-300':'border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            {/* Baffled */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Baffled tank</span>
              <button onClick={()=>setField('baffled',!cfg.baffled)}
                className={`relative w-10 h-5 rounded-full transition-colors ${cfg.baffled?'bg-amber-500':'bg-slate-700'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${cfg.baffled?'left-5':'left-0.5'}`}/>
              </button>
            </div>
          </div>

          {/* Mixing quality effect */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-amber-400"/>
              <h2 className="text-sm font-semibold text-slate-200">Mixing Effect on Water Quality</h2>
            </div>
            {(() => {
              const G=results.G, Gt_val=results.Gt;
              const turbIn=liquidParams.turbidity??0, tssIn=liquidParams.tss??0;
              let turbR=0, tssR=0;
              if(G>=10&&Gt_val>=1000){ turbR=Math.min(95,20+30*Math.log10(Math.max(1,Gt_val/1000))); tssR=turbR*0.9; }
              if(G>1500){ turbR=Math.max(0,turbR-(G-1500)/100); tssR=Math.max(0,tssR-(G-1500)/100); }
              return (
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Turbidity removal</span>
                    <span className="text-emerald-300 font-bold">{turbR.toFixed(0)}%</span>
                  </div>
                  <div className="bg-slate-800/40 rounded px-2 py-1.5 flex justify-between">
                    <span className="text-slate-500">In: {turbIn.toFixed(0)} NTU</span>
                    <span className="text-slate-300">→ {Math.max(0,turbIn*(1-turbR/100)).toFixed(0)} NTU</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">TSS removal</span>
                    <span className="text-emerald-300 font-bold">{tssR.toFixed(0)}%</span>
                  </div>
                  <div className="bg-slate-800/40 rounded px-2 py-1.5 flex justify-between">
                    <span className="text-slate-500">In: {tssIn.toFixed(0)} mg/L</span>
                    <span className="text-slate-300">→ {Math.max(0,tssIn*(1-tssR/100)).toFixed(0)} mg/L</span>
                  </div>
                  {G>1500&&(
                    <div className="flex items-start gap-1.5 bg-red-900/30 border border-red-700/40 rounded-lg p-2 text-red-300">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                      <span>High G &gt;1 500 s⁻¹ — floc breakup risk</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* ── RIGHT COLUMN: Tank + Params ────────────────────────────────── */}
        <div className="xl:col-span-1 space-y-4">

          {/* Tank configuration */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
            <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition-colors"
              onClick={()=>setShowTankPanel(p=>!p)}>
              <div className="flex items-center gap-2">
                <Beaker className="w-4 h-4 text-blue-400"/>
                <span className="text-sm font-semibold text-slate-200">Tank Configuration</span>
              </div>
              {showTankPanel?<ChevronUp className="w-4 h-4 text-slate-400"/>:<ChevronDown className="w-4 h-4 text-slate-400"/>}
            </button>
            {showTankPanel && (
              <div className="px-4 pb-4 space-y-4">
                {/* Shape */}
                <div>
                  <div className="text-xs text-slate-400 mb-2">Tank Shape</div>
                  <div className="grid grid-cols-3 gap-2">
                    {([['cylindrical','⭕ Cylindrical'],['square','🟦 Square'],['rectangular','▭ Rectangular']] as const).map(([v,l])=>(
                      <button key={v} onClick={()=>setField('tankShape',v)}
                        className={`py-2 rounded-lg border text-xs font-medium transition-colors ${cfg.tankShape===v?'border-blue-500 bg-blue-900/40 text-blue-300':'border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Volume */}
                <div>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-400">Tank Volume</span>
                    <span className="text-blue-300 font-mono font-bold">{cfg.tankVolume} m³</span>
                  </div>
                  <input type="range" min={0.1} max={500} step={0.1} value={cfg.tankVolume}
                    onChange={e=>setField('tankVolume',+e.target.value)} className="w-full accent-blue-400"/>
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {VOLUME_PRESETS.map(v=>(
                      <button key={v} onClick={()=>setField('tankVolume',v)}
                        className={`text-xs px-2 py-0.5 rounded border transition-colors ${cfg.tankVolume===v?'border-blue-500 bg-blue-900/40 text-blue-300':'border-slate-700 text-slate-400 hover:border-slate-500'}`}>
                        {v}m³
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2 items-center">
                    <span className="text-xs text-slate-500">Custom:</span>
                    <input type="number" min={0.1} max={10000} step={0.1} value={cfg.tankVolume}
                      onChange={e=>setField('tankVolume',Math.max(0.1,+e.target.value))}
                      className="w-24 bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-xs text-slate-200 font-mono"/>
                    <span className="text-xs text-slate-500">m³</span>
                  </div>
                </div>
                {/* H/D ratio */}
                <div>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-400">H/D Ratio</span>
                    <span className="text-blue-300 font-mono font-bold">{cfg.hdRatio.toFixed(2)}</span>
                  </div>
                  <input type="range" min={0.3} max={3.0} step={0.05} value={cfg.hdRatio}
                    onChange={e=>setField('hdRatio',+e.target.value)} className="w-full accent-blue-400"/>
                  <div className="flex justify-between text-xs text-slate-500 mt-0.5">
                    <span>0.3 (shallow)</span><span>Typical 0.8–1.2</span><span>3.0 (tall)</span>
                  </div>
                </div>
                {/* Computed dims */}
                <div className="bg-slate-800/40 rounded-lg p-3 text-xs text-slate-400 grid grid-cols-3 gap-2 text-center">
                  {[['T (m)', tankDims.T.toFixed(2)],['H (m)', tankDims.H.toFixed(2)],['D (mm)', (results.D*1000).toFixed(0)]].map(([l,v])=>(
                    <div key={l}>
                      <div className="text-slate-500">{l}</div>
                      <div className="text-slate-200 font-mono font-bold">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Liquid parameters */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
            <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition-colors"
              onClick={()=>setShowParamsPanel(p=>!p)}>
              <div className="flex items-center gap-2">
                <Wind className="w-4 h-4 text-green-400"/>
                <span className="text-sm font-semibold text-slate-200">Liquid Parameters</span>
                <span className="text-xs text-slate-500 ml-1">{dosingState?'(from dosing — adjustable)':'(manual entry)'}</span>
              </div>
              {showParamsPanel?<ChevronUp className="w-4 h-4 text-slate-400"/>:<ChevronDown className="w-4 h-4 text-slate-400"/>}
            </button>
            {showParamsPanel && (
              <div className="px-4 pb-4 space-y-3">
                {Object.entries(PARAM_META).map(([key,meta])=>{
                  const val=liquidParams[key]??0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-400">{meta.label}</span>
                        <span className="text-green-300 font-mono font-bold">
                          {key==='pH'?val.toFixed(1):val.toFixed(key==='conductivity'?0:1)}{meta.unit?` ${meta.unit}`:''}
                        </span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <input type="range" min={meta.min} max={meta.max} step={meta.step} value={val}
                          onChange={e=>setLiquidParams(prev=>({...prev,[key]:+e.target.value}))}
                          className="flex-1 accent-green-400"/>
                        <input type="number" min={meta.min} max={meta.max} step={meta.step} value={val}
                          onChange={e=>setLiquidParams(prev=>({...prev,[key]:+e.target.value}))}
                          className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-0.5 text-xs text-slate-200 font-mono text-right"/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Proceed */}
          <button onClick={handleProceedToTrain}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-sm transition-colors">
            <LayoutDashboard className="w-4 h-4"/>
            Save & View Full Process Train
            <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </div>
  );
}
